import {
  ApplicationConfig,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
  inject,
} from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideServiceWorker } from '@angular/service-worker';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { providePrimeNG } from 'primeng/config';
import Aura from '@primeuix/themes/aura';
import { firstValueFrom, take } from 'rxjs';
import { provideStore, Store } from '@ngrx/store';
import { Actions, provideEffects, ofType } from '@ngrx/effects';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { routes } from './app.routes';
import { environment } from '../environments/environment';
import { AdministrationApiConfiguration } from './core/api/generated/administration/administration-api-configuration';
import { AmcApiConfiguration } from './core/api/generated/amc/amc-api-configuration';
import { IdentityApiConfiguration } from './core/api/generated/identity/identity-api-configuration';
import { RuntimeConfigService } from './core/config/runtime-config.service';
import { authInterceptor } from './core/auth/auth.interceptor';
import { authRefreshInterceptor } from './core/auth/auth-refresh.interceptor';
import { correlationIdInterceptor } from './core/interceptors/correlation-id.interceptor';
import { errorInterceptor } from './core/interceptors/error.interceptor';
import { loggingInterceptor } from './core/interceptors/logging.interceptor';
import { toastInterceptor } from './core/interceptors/toast.interceptor';
import { AuthStorageSync } from './core/auth/auth-storage-sync';
import { AuthActions } from './core/state/auth/auth.actions';
import { AuthEffects } from './core/state/auth/auth.effects';
import { authReducer } from './core/state/auth/auth.reducer';
import { UserEffects } from './core/state/user/user.effects';
import { userReducer } from './core/state/user/user.reducer';
import { uiReducer } from './core/state/ui/ui.reducer';
import { SyncQueueEffects } from './core/state/sync-queue/sync-queue.effects';
import { syncQueueReducer } from './core/state/sync-queue/sync-queue.reducer';

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideRouter(routes),
    provideHttpClient(
      withInterceptors([
        correlationIdInterceptor,
        loggingInterceptor,
        authInterceptor,
        toastInterceptor,
        authRefreshInterceptor,
        errorInterceptor,
      ]),
    ),
    provideAnimationsAsync(),
    provideStore({
      auth: authReducer,
      user: userReducer,
      ui: uiReducer,
      syncQueue: syncQueueReducer,
    }),
    provideEffects(AuthEffects, UserEffects, SyncQueueEffects),
    provideStoreDevtools({
      maxAge: 25,
      logOnly: environment.production,
    }),
    providePrimeNG({
      theme: {
        preset: Aura,
        options: {
          darkModeSelector: false,
        },
      },
    }),
    provideAppInitializer(() => {
      const store = inject(Store);
      const actions$ = inject(Actions);
      const storageSync = inject(AuthStorageSync);
      const runtime = inject(RuntimeConfigService);
      const identityApi = inject(IdentityApiConfiguration);
      const amcApi = inject(AmcApiConfiguration);
      const administrationApi = inject(AdministrationApiConfiguration);

      return (async () => {
        // API URLs come from /runtime-config.json (rendered at container start),
        // so they must be applied before any request is issued.
        await runtime.load();
        identityApi.rootUrl = runtime.identity;
        amcApi.rootUrl = runtime.backend;
        administrationApi.rootUrl = runtime.backend;

        const initialized = firstValueFrom(
          actions$.pipe(
            ofType(AuthActions.initializeSucceeded, AuthActions.initializeFailed),
            take(1),
          ),
        );
        store.dispatch(AuthActions.initializeRequested());
        await initialized;
        storageSync.start();
      })();
    }),
    provideServiceWorker('ngsw-worker.js', {
      enabled: environment.serviceWorker,
      registrationStrategy: 'registerWhenStable:5000',
    }),
  ],
};
